import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

export default function SubscribeModal({ open, onOpenChange, onSuccess }) {
  const [config, setConfig] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!config) api.get("/payment/config").then((r) => setConfig(r.data)).catch(() => {});
    if (!planId) api.get("/payment/plan").then((r) => setPlanId(r.data.plan_id)).catch(() => {});
  }, [open, config, planId]);

  const activate = async (subscription_id) => {
    setProcessing(true);
    const finish = () => {
      toast.success("Welcome to YBTV Premium! Unlocking your library…");
      // Hard reload — guarantees fresh AuthContext + content state across mobile + desktop.
      setTimeout(() => { window.location.replace("/browse"); }, 1000);
    };
    try {
      await api.post("/payment/paypal/subscription/activate", { subscription_id });
      finish();
      return;
    } catch (_) {
      // Cookie may have been stripped — try the public claim endpoint.
    }
    try {
      const r = await api.post("/payment/claim", { subscription_id });
      if (r.data?.claimed) { finish(); return; }
    } catch (_) {}
    for (let i = 0; i < 20; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 3000));
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await api.get("/auth/me");
        if (r.data?.is_subscribed) { finish(); return; }
      } catch (_) {}
      try {
        // eslint-disable-next-line no-await-in-loop
        const r2 = await api.post("/payment/claim", { subscription_id });
        if (r2.data?.claimed) { finish(); return; }
      } catch (_) {}
    }
    setProcessing(false);
    toast.error("Payment received but activation is taking longer than usual. Refresh in a moment or email support@youngblack.tv.");
  };

  const startDemoCheckout = async () => {
    try {
      setProcessing(true);
      const r = await api.post("/payment/paypal/create-order");
      const c = await api.post("/payment/paypal/capture", { order_id: r.data.order_id });
      toast.success("Welcome to YBTV Premium!");
      onSuccess?.(c.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payment failed.");
    } finally {
      setProcessing(false);
    }
  };

  const live = config?.configured && config?.paypal_client_id && planId;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="subscribe-modal"
        className="bg-[#141419] border-white/10 text-white max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">
            Unlock the full library
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Auto-renews monthly. Cancel anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-6 my-2">
          <div className="flex items-baseline gap-1">
            <span className="font-display text-5xl font-black text-white">$3.99</span>
            <span className="text-gray-400">/month</span>
          </div>
          <div className="mt-1 text-xs text-[#FFD700] uppercase tracking-[0.2em]">YBTV Premium</div>

          <ul className="mt-5 space-y-2.5 text-sm text-gray-300">
            {[
              "All movies, shows, trailers & interviews",
              "Watch in HD on any device",
              "New releases every week",
              "Cancel anytime",
            ].map((f) => (
              <li key={f} className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#E50914]" /> {f}
              </li>
            ))}
          </ul>
        </div>

        {processing && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-300 py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Working…
          </div>
        )}

        {live ? (
          <div data-testid="paypal-buttons-wrap" className={processing ? "opacity-50 pointer-events-none" : ""}>
            <PayPalScriptProvider
              options={{
                clientId: config.paypal_client_id,
                vault: true,
                intent: "subscription",
                currency: "USD",
              }}
            >
              <PayPalButtons
                style={{ layout: "vertical", shape: "pill", color: "gold", label: "subscribe" }}
                createSubscription={(data, actions) =>
                  actions.subscription.create({ plan_id: planId })
                }
                onApprove={async (data) => {
                  await activate(data.subscriptionID);
                }}
                onError={(err) => {
                  console.error("PayPal error:", err);
                  toast.error("PayPal checkout error.");
                }}
                onCancel={() => toast.message("Checkout cancelled")}
              />
            </PayPalScriptProvider>
          </div>
        ) : (
          <Button
            data-testid="paypal-subscribe-btn"
            onClick={startDemoCheckout}
            disabled={processing}
            className="w-full rounded-full bg-[#E50914] hover:bg-[#F40612] text-white font-semibold py-6 text-base"
          >
            {processing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</> : <>Pay $3.99 (demo)</>}
          </Button>
        )}

        <p className="text-[11px] text-gray-500 text-center leading-relaxed">
          Secure checkout via PayPal. By subscribing you agree to recurring monthly billing of $3.99 until you cancel.
        </p>
      </DialogContent>
    </Dialog>
  );
}
